import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, MapPin, Calendar, BookOpen, FileText, Download, Eye, X, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/api/api';
const StudentDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const studentId = searchParams.get('id');
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [previewDocument, setPreviewDocument] = useState(null);

  useEffect(() => {
    if (studentId) {
      fetchStudentDetails();
    }
  }, [studentId]);

  const fetchStudentDetails = async () => {
    try {
      const response = await api.get(`/students/${studentId}`);
      console.log('📄 Student data loaded:', response.data);
      console.log('📁 Documents:', response.data.documents);
      setStudent(response.data);
    } catch (error) {
      console.error('Error fetching student details:', error);
      alert('Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  // Get document display name
  const getDocumentDisplayName = (key) => {
    const names = {
      'photo': 'Passport Photo',
      'profile_photo': 'Profile Photo',
      'aadhar': 'Aadhar Card',
      'aadhar_card': 'Aadhar Card',
      'class_10_marksheet': '10th Marksheet',
      '10th_marksheet': '10th Marksheet',
      'class_10_certificate': '10th Certificate',
      '10th_certificate': '10th Certificate',
      'class_12_marksheet': '12th Marksheet',
      '12th_marksheet': '12th Marksheet',
      'class_12_certificate': '12th Certificate',
      '12th_certificate': '12th Certificate',
      'caste_certificate': 'Caste Certificate',
      'income_certificate': 'Income Certificate',
      'domicile_certificate': 'Domicile Certificate',
      'migration_certificate': 'Migration Certificate',
      'transfer_certificate': 'Transfer Certificate',
      'character_certificate': 'Character Certificate',
      'diploma_marksheet': 'Diploma Marksheet',
      'diploma_certificate': 'Diploma Certificate',
      'graduation_marksheet': 'Graduation Marksheet',
      'graduation_certificate': 'Graduation Certificate'
    };
    return names[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Check if a document is an image
  const isImageDocument = (docData) => {
    if (!docData) return false;
    const data = typeof docData === 'string' ? docData : docData.data || docData.url || docData;
    if (typeof data !== 'string') return false;
    return data.startsWith('data:image') || 
           data.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
           data.includes('image/');
  };

  // Get document data URL
  const getDocumentUrl = (docData) => {
    if (!docData) return null;
    if (typeof docData === 'string') return docData;
    return docData.data || docData.url || null;
  };

  // Render documents from student record
  const renderDocuments = () => {
    const documents = student?.documents || {};
    const documentKeys = Object.keys(documents).filter(key => {
      const doc = documents[key];
      return doc && (typeof doc === 'string' ? doc.length > 0 : doc.data || doc.url);
    });

    if (documentKeys.length === 0) {
      return (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-600">No documents uploaded</p>
          <p className="text-sm text-gray-500">Documents will appear here after upload during admission</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentKeys.map((key, index) => {
          const docData = documents[key];
          const docUrl = getDocumentUrl(docData);
          const isImage = isImageDocument(docData);
          const displayName = getDocumentDisplayName(key);

          return (
            <div 
              key={index}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {isImage ? (
                    <div className="w-12 h-12 bg-white rounded border overflow-hidden">
                      <img 
                        src={docUrl} 
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg></div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{displayName}</p>
                    <p className="text-xs text-gray-500">{isImage ? 'Image' : 'Document'}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setPreviewDocument({ name: displayName, url: docUrl, isImage })}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = docUrl;
                    link.download = `${student.admission_number}_${key}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Student not found</p>
        <Button
          onClick={() => navigate('/students/list')}
          className="mt-4"
        >
          Back to Students
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'academic', label: 'Academic Info', icon: BookOpen },
    { id: 'guardian', label: 'Family Info', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="student-details-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/students/list')}
          className="flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Student Details
          </h1>
          <p className="text-gray-600 mt-1">View complete student information</p>
        </div>
      </div>

      {/* Student Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-6">
          {/* Profile Photo - Show uploaded photo from documents or direct fields */}
          {(() => {
            // Helper to extract photo URL from documents
            const getPhotoFromDocuments = () => {
              const docs = student.documents;
              if (!docs || typeof docs !== 'object') return null;
              
              const photoKeys = ['passport_photo', 'profile_photo', 'photo', 'passport_size_photo'];
              for (const key of photoKeys) {
                const doc = docs[key];
                if (doc) {
                  // Handle nested object format: {data: 'base64...', filename: '...'}
                  if (typeof doc === 'object' && doc.data) {
                    return doc.data;
                  }
                  // Handle simple string format
                  if (typeof doc === 'string' && (doc.startsWith('data:') || doc.startsWith('http'))) {
                    return doc;
                  }
                }
              }
              return null;
            };
            
            const photoUrl = student.profile_photo || student.photo_url || getPhotoFromDocuments();
            
            return photoUrl ? (
              <img 
                src={photoUrl} 
                alt={student.first_name}
                className="w-24 h-24 rounded-full object-cover border-4 border-blue-100"
              />
            ) : (
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="text-blue-600" size={48} />
              </div>
            );
          })()}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {student.full_name || `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim()}
            </h2>
            <p className="text-gray-600 mt-1">
              Admission No: <span className="font-semibold text-blue-600">{student.admission_number || student.admission_no || student.student_id || 'N/A'}</span>
              {student.roll_number && (
                <span className="ml-3" data-testid="student-details-roll-number">
                  | College Roll No: <span className="font-semibold text-purple-700">{student.roll_number}</span>
                </span>
              )}
              {student.university_id && (
                <span className="ml-3" data-testid="student-details-university-id">
                  | University ID: <span className="font-semibold text-teal-700">{student.university_id}</span>
                </span>
              )}
              {student.is_lateral_entry && (
                <span
                  className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                  data-testid="student-details-lateral-badge"
                >
                  🔀 LATERAL ENTRY{student.entry_semester ? ` · Started Sem ${student.entry_semester}` : ''}
                </span>
              )}
            </p>
            <div className="flex gap-4 mt-2 flex-wrap">
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                student.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {student.status || 'Active'}
              </span>
              {/* Only show course badge if course name exists */}
              {(student.course_name || student.course?.name) && (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {student.course_name || student.course?.name}
                </span>
              )}
              {/* Show religion and caste if available */}
              {(student.religion) && (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                  {student.religion}
                </span>
              )}
              {(student.caste || student.caste_category) && (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                  {student.caste || student.caste_category}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex gap-4 px-6 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Mail className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{student.email || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{student.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Date of Birth</p>
                  <p className="font-medium text-gray-900">{student.date_of_birth || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Gender</p>
                  <p className="font-medium text-gray-900">{student.gender || 'N/A'}</p>
                </div>
              </div>
              {student.blood_group && (
                <div className="flex items-start gap-3">
                  <User className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Blood Group</p>
                    <p className="font-medium text-gray-900">{student.blood_group}</p>
                  </div>
                </div>
              )}
              {student.aadhar_no && (
                <div className="flex items-start gap-3">
                  <FileText className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Aadhar Number</p>
                    <p className="font-medium text-gray-900">{student.aadhar_no}</p>
                  </div>
                </div>
              )}
              {student.medium_of_instruction && (
                <div className="flex items-start gap-3">
                  <BookOpen className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Medium of Instruction</p>
                    <p className="font-medium text-gray-900">{student.medium_of_instruction}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">
                    {[student.address, student.city, student.state, student.pin_code].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Academic Info Tab */}
          {activeTab === 'academic' && (
            <div className="space-y-6">
              {/* Current Course Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <BookOpen className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Course</p>
                    <p className="font-medium text-gray-900">{student.course_name || student.course?.name || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Semester</p>
                    <p className="font-medium text-gray-900">{student.semester_name || student.semester?.name || student.current_semester || 'Semester 1'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Section</p>
                    <p className="font-medium text-gray-900">{student.section_name || student.section?.name || 'Section A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Admission Date</p>
                    <p className="font-medium text-gray-900">{student.admission_date || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* 10th Details */}
              {(student.class_10_board || student.class_10_school) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3">10th Standard</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {student.class_10_board && (
                      <div>
                        <p className="text-sm text-gray-500">Board</p>
                        <p className="font-medium text-gray-900">{student.class_10_board}</p>
                      </div>
                    )}
                    {student.class_10_school && (
                      <div>
                        <p className="text-sm text-gray-500">School</p>
                        <p className="font-medium text-gray-900">{student.class_10_school}</p>
                      </div>
                    )}
                    {student.class_10_year && (
                      <div>
                        <p className="text-sm text-gray-500">Year</p>
                        <p className="font-medium text-gray-900">{student.class_10_year}</p>
                      </div>
                    )}
                    {student.class_10_percentage && (
                      <div>
                        <p className="text-sm text-gray-500">Percentage</p>
                        <p className="font-medium text-gray-900">{student.class_10_percentage}%</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 12th Details */}
              {(student.class_12_board || student.class_12_school) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3">12th Standard</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {student.class_12_board && (
                      <div>
                        <p className="text-sm text-gray-500">Board</p>
                        <p className="font-medium text-gray-900">{student.class_12_board}</p>
                      </div>
                    )}
                    {student.class_12_school && (
                      <div>
                        <p className="text-sm text-gray-500">School</p>
                        <p className="font-medium text-gray-900">{student.class_12_school}</p>
                      </div>
                    )}
                    {student.class_12_year && (
                      <div>
                        <p className="text-sm text-gray-500">Year</p>
                        <p className="font-medium text-gray-900">{student.class_12_year}</p>
                      </div>
                    )}
                    {student.class_12_percentage && (
                      <div>
                        <p className="text-sm text-gray-500">Percentage</p>
                        <p className="font-medium text-gray-900">{student.class_12_percentage}%</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Diploma Details (for lateral entry) */}
              {(student.diploma_name || student.diploma_college) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Diploma (Lateral Entry)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {student.diploma_name && (
                      <div>
                        <p className="text-sm text-gray-500">Diploma Name</p>
                        <p className="font-medium text-gray-900">{student.diploma_name}</p>
                      </div>
                    )}
                    {student.diploma_college && (
                      <div>
                        <p className="text-sm text-gray-500">College</p>
                        <p className="font-medium text-gray-900">{student.diploma_college}</p>
                      </div>
                    )}
                    {student.diploma_year && (
                      <div>
                        <p className="text-sm text-gray-500">Year</p>
                        <p className="font-medium text-gray-900">{student.diploma_year}</p>
                      </div>
                    )}
                    {student.diploma_percentage && (
                      <div>
                        <p className="text-sm text-gray-500">Percentage</p>
                        <p className="font-medium text-gray-900">{student.diploma_percentage}%</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Guardian Info Tab */}
          {activeTab === 'guardian' && (
            <div className="space-y-6">
              {/* Father's Info */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <User size={18} className="text-blue-600" />
                  Father's Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{student.father_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{student.father_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Occupation</p>
                    <p className="font-medium text-gray-900">{student.father_occupation || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Mother's Info */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <User size={18} className="text-pink-600" />
                  Mother's Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{student.mother_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{student.mother_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Occupation</p>
                    <p className="font-medium text-gray-900">{student.mother_occupation || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Guardian Info (if different) */}
              {(student.guardian_name && student.guardian_name !== student.father_name) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Users size={18} className="text-green-600" />
                    Guardian's Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{student.guardian_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{student.guardian_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Relation</p>
                      <p className="font-medium text-gray-900">{student.guardian_relation || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div data-testid="documents-section">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">Uploaded Documents</h3>
                <p className="text-sm text-gray-500">
                  {Object.keys(student?.documents || {}).filter(k => student.documents[k]).length} document(s)
                </p>
              </div>
              {renderDocuments()}
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-semibold text-gray-800">{previewDocument.name}</h3>
              <button 
                onClick={() => setPreviewDocument(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-100px)]">
              {previewDocument.isImage ? (
                <img 
                  src={previewDocument.url} 
                  alt={previewDocument.name}
                  className="max-w-full mx-auto"
                />
              ) : (
                <iframe 
                  src={previewDocument.url}
                  title={previewDocument.name}
                  className="w-full h-[70vh]"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetails;
